import { ELEMENTS } from "./balance";
import type { Board } from "./board";
import { createOpponentBoard, type Run } from "./run";

export const SAVE_KEY = "mergeborn-run";
export const serializeRun = (run: Run): string => JSON.stringify({ version: 1, run });

function validBoard(value: unknown): value is Board {
  if (!value || typeof value !== "object") return false;
  const board = value as Board;
  if (board.width !== 12 || board.height !== 12 || !Array.isArray(board.cells) || board.cells.length !== 144) return false;
  const ids = new Set<string>();
  return board.cells.every((piece) => {
    if (piece === null) return true;
    if (!piece || typeof piece.id !== "string" || !piece.id || ids.has(piece.id) ||
      !ELEMENTS.includes(piece.element) || !Number.isSafeInteger(piece.tier) || piece.tier < 1) return false;
    ids.add(piece.id);
    return true;
  }) && ids.size > 0;
}

export function deserializeRun(raw: string | null): Run | null {
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw);
    // Recover the former single-board checkpoint with a stable authored opponent.
    if (saved.version === 0 && validBoard(saved.board)) {
      const level = Number.isSafeInteger(saved.level) && saved.level > 0 ? saved.level : 1;
      const legacyBoard = saved.board as Board;
      const cells = legacyBoard.cells.map((piece, i) => piece ? { ...piece, id: `piece-${i + 1}` } : null);
      return { phase: "board", level, playerBoard: { ...saved.board, cells }, opponentBoard: createOpponentBoard(level),
        rngState: 2048, nextId: 145, lastResult: null, attempts: 1 };
    }
    const run = saved.run as Run;
    if (saved.version !== 1 || !run || !["board", "reward", "battle"].includes(run.phase) ||
      !Number.isSafeInteger(run.level) || run.level < 1 ||
      !Number.isInteger(run.rngState) || run.rngState < 0 || run.rngState > 0xffffffff ||
      !Number.isSafeInteger(run.nextId) || run.nextId < 2 ||
      ![null, "player", "opponent", "draw"].includes(run.lastResult) ||
      !validBoard(run.playerBoard) || !validBoard(run.opponentBoard)) return null;
    if (run.attempts !== undefined && (!Number.isSafeInteger(run.attempts) || run.attempts < 1)) return null;
    if (run.battle !== undefined) {
      const progress = run.battle;
      if (!progress || !Number.isFinite(progress.elapsed) || progress.elapsed < 0 || progress.elapsed > 91_000 ||
        !Array.isArray(progress.playerInputs) || progress.playerInputs.length > 10_000 ||
        !progress.playerInputs.every((at, i) => Number.isFinite(at) && at >= 0 && at <= progress.elapsed &&
          (i === 0 || at >= progress.playerInputs[i - 1]))) return null;
    }
    const nextId = Math.max(run.nextId, ...run.playerBoard.cells.map((piece) =>
      piece?.id.match(/^piece-(\d+)$/) ? Number(piece.id.slice(6)) + 1 : 2));
    return { ...run, nextId, attempts: run.attempts ?? 1 };
  } catch { return null; }
}
